import type { EmailAddressListResponse } from "@/schema"
import { useInfiniteQuery, useQuery } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"
import { useBookId } from "@/hooks/use-book-id"

import { fetchEmailAddressSuggestions, fetchUnsubscribedEmailAddresses } from "./api"

export function useUnsubscribedEmailAddresses() {
  const getContext = useApiContext()

  return useInfiniteQuery<
    EmailAddressListResponse,
    Error,
    {
      email_addresses: EmailAddressListResponse["email_addresses"]
      pagination: EmailAddressListResponse["pagination"]
    },
    string[]
  >({
    queryKey: ["email_addresses", "unsubscribed"],
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.pagination?.next_page_params ?? undefined,
    select: (data) => ({
      email_addresses: data.pages.flatMap((p) => p.email_addresses),
      pagination: data.pages[data.pages.length - 1]?.pagination,
    }),
    queryFn: async ({ pageParam }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchUnsubscribedEmailAddresses(context, pageParam as { page?: number; per_page?: number })
    },
  })
}

export function useFetchEmailAddressSuggestions(query: string) {
  const bookId = useBookId()
  const getContext = useApiContext()
  const trimmedQuery = query.trim()

  return useQuery({
    queryKey: ["email-addresses", "suggestions", bookId, trimmedQuery],
    queryFn: async () => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return fetchEmailAddressSuggestions(context, trimmedQuery)
    },
    enabled: !!bookId && trimmedQuery.length > 0,
    staleTime: 1000 * 60 * 5,
  })
}
